import { ReactNode, createContext, useCallback, useEffect, useState } from "react";
import { queryClient } from "../api";
import { useIndexQuery } from "../api/genericHooks";
import { Anecdote, Answer, Book, Chapter, Question } from "../types/models";
import { GeneratedChapter } from "../types/models/GeneratedChapter";
import { Language } from "../types/models/Language";
import { Medium } from "../types/models/Medium";
import { NarrationStyle } from "../types/models/NarrationStyle";
import { PrintOrder } from "../types/models/PrintOrder";
import { Subscription } from "../types/models/Subscription";

export type BookContextProps = {
  book: Book | null;
  chapters: Chapter[];
  generated_chapters: GeneratedChapter[];
  questions: Question[];
  answers: Answer[];
  media: Medium[];
  anecdotes: Anecdote[];
  narration_styles: NarrationStyle[];
  languages: Language[];
  subscription: Subscription | null;
  print_orders: PrintOrder[] | null;
  clear: () => void;
  setChapters: (chapters: Chapter[]) => void;
  setGeneratedChapters: (chapters: GeneratedChapter[]) => void;
  setQuestions: (questions: Question[]) => void;
  setAnswers: (questions: Answer[]) => void;
  setMedia: (media: Medium[]) => void;
  setAnecdotes: (anecdotes: Anecdote[]) => void;
  setBook: (book: Book) => void;
  setNarrationStyles: (narration_styles: NarrationStyle[]) => void;
  setLanguages: (languages: Language[]) => void;
  setSubscription: (subscription: Subscription) => void;
  setPrintOrders: (print_orders: PrintOrder[]) => void;
  refresh: () => void;
};

const initialEmptyState: BookContextProps = {
  book: null,
  chapters: [],
  questions: [],
  generated_chapters: [],
  answers: [],
  media: [],
  anecdotes: [],
  narration_styles: [],
  languages: [],
  subscription: null,
  print_orders: null,
  clear: () => {},
  setChapters: () => {},
  setGeneratedChapters: () => {},
  setQuestions: () => {},
  setAnswers: () => {},
  setMedia: () => {},
  setAnecdotes: () => {},
  setNarrationStyles: () => {},
  setLanguages: () => {},
  setBook: () => {},
  setSubscription: () => {},
  setPrintOrders: () => {},
  refresh: () => {},
};

const initialState: BookContextProps = {
  book: JSON.parse(localStorage.getItem("book") || "null"),
  chapters: JSON.parse(localStorage.getItem("chapters") || "[]"),
  generated_chapters: JSON.parse(
    localStorage.getItem("generated_chapters") || "[]"
  ),
  questions: JSON.parse(localStorage.getItem("questions") || "[]"),
  answers: JSON.parse(localStorage.getItem("answers") || "[]"),
  media: JSON.parse(localStorage.getItem("media") || "[]"),
  anecdotes: JSON.parse(localStorage.getItem("anecdotes") || "[]"),
  narration_styles: JSON.parse(
    localStorage.getItem("narration_styles") || "[]"
  ),
  languages: JSON.parse(localStorage.getItem("languages") || "[]"),
  subscription: JSON.parse(localStorage.getItem("subscription") || "null"),
  print_orders: JSON.parse(localStorage.getItem("print_orders") || "[]"),
  clear: () => {},
  setChapters: () => {},
  setGeneratedChapters: () => {},
  setQuestions: () => {},
  setAnswers: () => {},
  setNarrationStyles: () => {},
  setLanguages: () => {},
  setMedia: () => {},
  setAnecdotes: () => {},
  setBook: () => {},
  setSubscription: () => {},
  setPrintOrders: () => {},
  refresh: () => {},
};

const BookContext = createContext(initialState);

type BookProviderProps = {
  children: ReactNode;
};

function BookProvider({ children }: BookProviderProps) {
  const [bookStruct, setBookStruct] = useState<BookContextProps>(initialState);

  const setChapters = (chapters: Chapter[]) => {
    setBookStruct((prev) => ({ ...prev, chapters }));
    localStorage.setItem("chapters", JSON.stringify(chapters));
  };

  const setGeneratedChapters = (generated_chapters: GeneratedChapter[]) => {
    setBookStruct((prev) => ({ ...prev, generated_chapters }));
    localStorage.setItem(
      "generated_chapters",
      JSON.stringify(generated_chapters)
    );
  };

  const setQuestions = (questions: Question[]) => {
    setBookStruct((prev) => ({ ...prev, questions }));
    localStorage.setItem("questions", JSON.stringify(questions));
  };

  const setBook = (book: Book) => {
    setBookStruct((prev) => ({ ...prev, book }));
    localStorage.setItem("book", JSON.stringify(book));
  };

  const setSubscritpion = (subscription: Subscription) => {
    setBookStruct((prev) => ({ ...prev, subscription }));
    localStorage.setItem("subscription", JSON.stringify(subscription));
  };

  const setAnswers = (answers: Answer[]) => {
    setBookStruct((prev) => ({ ...prev, answers }));
    localStorage.setItem("answers", JSON.stringify(answers));
  };

  const setMedia = (media: Medium[]) => {
    setBookStruct((prev) => ({ ...prev, media }));
    localStorage.setItem("media", JSON.stringify(media));
  };

  const setAnecdotes = (anecdotes: Anecdote[]) => {
    setBookStruct((prev) => ({ ...prev, anecdotes }));
    localStorage.setItem("anecdotes", JSON.stringify(anecdotes));
  };

  const setNarrationStyles = (narration_styles: NarrationStyle[]) => {
    setBookStruct((prev) => ({ ...prev, narration_styles }));
    localStorage.setItem("narration_styles", JSON.stringify(narration_styles));
  };

  const setLanguages = (languages: Language[]) => {
    setBookStruct((prev) => ({ ...prev, languages }));
    localStorage.setItem("languages", JSON.stringify(languages));
  };

  const setPrintOrders = (print_orders: PrintOrder[]) => {
    setBookStruct((prev) => ({ ...prev, print_orders }));
    localStorage.setItem("print_orders", JSON.stringify(print_orders));
  };

  const clear = () => {
    localStorage.removeItem("book");
    localStorage.removeItem("chapters");
    localStorage.removeItem("generated_chapters");
    localStorage.removeItem("questions");
    localStorage.removeItem("answers");
    localStorage.removeItem("media");
    localStorage.removeItem("anecdotes");
    localStorage.removeItem("narration_styles");
    localStorage.removeItem("languages");
    localStorage.removeItem("subscription");
    localStorage.removeItem("print_orders");
    setBookStruct(initialEmptyState);
  };

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["/user_book"] });
  }, []);

  const { data } = useIndexQuery("/user_book", null, {
    enabled: true,
    staleTime: 1000 * 60 * 60, // Les données sont considérées fraîches pendant 1 heure
    cacheTime: 1000 * 60 * 60 * 24, // Les données restent en cache pendant 24 heures
  });

  // Sync data to state when query data changes
  useEffect(() => {
    if (!data) return;

    if (data.book) {
      setBook(data.book);
    }
    if (data.chapters) {
      setChapters(data.chapters);
    }
    if (data.generated_chapters) {
      setGeneratedChapters(data.generated_chapters);
    }
    if (data.questions) {
      setQuestions(data.questions);
    }
    if (data.answers) {
      setAnswers(data.answers);
    }
    if (data.media) {
      setMedia(data.media);
    }
    if (data.anecdotes) {
      setAnecdotes(data.anecdotes);
    }
    if (data.narration_styles) {
      setNarrationStyles(data.narration_styles);
    }
    if (data.languages) {
      setLanguages(data.languages);
    }
    if (data.subscription) {
      setSubscritpion(data.subscription);
    }
    if (data.print_orders) {
      setPrintOrders(data.print_orders);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const value = {
    ...bookStruct,
    clear,
    setChapters,
    setGeneratedChapters,
    setQuestions,
    setBook,
    setMedia,
    setAnecdotes,
    setNarrationStyles,
    setLanguages,
    setAnswers,
    setSubscritpion,
    setPrintOrders,
    refresh,
  };

  return <BookContext.Provider value={value}>{children}</BookContext.Provider>;
}

export { BookContext, BookProvider };
